import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FONTS, TYPE } from 'style/typography';
import { isQuikbaseContextOnWeb } from 'utils/webRedirects';
import ProgressBarMinimal from '../ui/ProgressBarMinimal';
import { SCENE_DONE, UNITY_SHARE, loadProgress, onLoadProgress, resetLoadProgress } from './loadProgress';
import { loadingDestination } from './loadingDestination';
import { loadingTrip, onLoadingTrip } from './loadingTrip';
import PlainLoading from './Loading.js';

/**
 * The world's loading screen on the web. Owner 13 Oct: "same old progress bar, just with the unit's image and title will
 * suffice" - the original screen (black, "Loading...", the lime ProgressBarMinimal with its percentage) unchanged, with
 * the unit's picture and title above it while entering a unit (Quikbase's for Quikbase, nothing when not known yet).
 * The percentage is the real load (loadProgress.js): between milestones it only creeps to just below the next one.
 * The mobile app keeps Loading.js.
 */
const ceilingFor = real => (real < UNITY_SHARE ? UNITY_SHARE - 2 : real < SCENE_DONE ? SCENE_DONE - 2 : 97);

const WorldLoading = ({ loading }) => {
  const [real, setReal] = useState(loadProgress());
  const [creep, setCreep] = useState(0);
  const [visible, setVisible] = useState(loading);
  const wasLoading = useRef(loading);
  const tokenValues = useSelector(state => state.app.courseTokenValues);
  const courseData = useSelector(state => state.course.courseData);

  const [trip, setTrip] = useState(loadingTrip());
  useEffect(() => onLoadProgress(setReal), []);
  useEffect(() => onLoadingTrip(setTrip), []);

  useEffect(() => {
    if (loading && !wasLoading.current) {
      resetLoadProgress();
      setCreep(0);
    }
    wasLoading.current = loading;
    if (loading) {
      setVisible(true);
      return undefined;
    }
    // as before: the bar shows 100% for a moment, then the screen goes
    const gone = setTimeout(() => setVisible(false), 600);
    return () => clearTimeout(gone);
  }, [loading]);

  useEffect(() => {
    if (!loading) return undefined;
    const tick = setInterval(() => setCreep(c => Math.min(Math.max(c, real) + 0.5, ceilingFor(real))), 500);
    return () => clearInterval(tick);
  }, [loading, real]);

  if (!visible) return null;
  const to = loadingDestination({ quikbase: isQuikbaseContextOnWeb(), tokenValues, courseData, trip });
  const percent = loading ? Math.round(Math.max(real, creep)) : 100;
  return (
    <div
      data-testid="world-loading"
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {to.image ? (
          <img src={to.image} alt="" style={{ width: 200, height: 150, objectFit: 'cover', objectPosition: '50% 30%', borderRadius: 12, marginBottom: 16 }} />
        ) : null}
        {to.title ? (
          <div style={{ fontFamily: FONTS.body, fontSize: TYPE.xl, fontWeight: 600, color: '#ffffff', textAlign: 'center', maxWidth: 480, marginBottom: 24 }}>
            {to.title}
          </div>
        ) : null}
        <ProgressBarMinimal progress={percent} label="Loading…" />
      </div>
    </div>
  );
};

WorldLoading.propTypes = { loading: PropTypes.bool.isRequired };

// Other overlays (the avatar's appearance, webcam captures) pass their own colour or label: they keep the plain bar.
const Loading = ({ loading, color, label }) =>
  color || label ? <PlainLoading loading={loading} color={color} label={label} /> : <WorldLoading loading={loading} />;

Loading.propTypes = {
  loading: PropTypes.bool.isRequired,
  color: PropTypes.string,
  label: PropTypes.string,
};

export default Loading;
